import { CreditApplicationStatus, OrderType, PaymentStatus } from "~/gql/graphqlBase.generated";
import { formatPaymentStatus } from "~/helpers/paymentStatus";
import i18n from "~/i18n/i18n";

const CREDIT_APPLICATION_STATUS_LABEL_KEYS: Record<CreditApplicationStatus, string> = {
	[CreditApplicationStatus.Applied]: "orders.creditApplicationStatus.applied",
	[CreditApplicationStatus.PartiallyApplied]: "orders.creditApplicationStatus.partiallyApplied",
	[CreditApplicationStatus.Unapplied]: "orders.creditApplicationStatus.unapplied"
};

export interface OrderMoneyStatusFields {
	creditApplicationStatus?: CreditApplicationStatus | null;
	orderType?: null | OrderType;
	paymentStatus?: null | PaymentStatus;
}

export type OrderMoneyStatus =
	| { family: "creditApplication"; label: string; status: CreditApplicationStatus }
	| { family: "payment"; label: string; status: PaymentStatus };

export const formatCreditApplicationStatus = (value: unknown): string => {
	const key = CREDIT_APPLICATION_STATUS_LABEL_KEYS[value as CreditApplicationStatus];
	return key ? i18n.t(key, { ns: "fulfill" }) : "";
};

export const isCreditApplicationStatus = (value: unknown): value is CreditApplicationStatus =>
	Object.values(CreditApplicationStatus).includes(value as CreditApplicationStatus);

export const formatOrderMoneyStatusValue = (value: unknown): string =>
	isCreditApplicationStatus(value) ? formatCreditApplicationStatus(value) : formatPaymentStatus(value);

export const isCreditOrder = (order: Pick<OrderMoneyStatusFields, "orderType">): boolean =>
	order.orderType === OrderType.Credit;

export const resolveOrderMoneyStatus = (order: OrderMoneyStatusFields): null | OrderMoneyStatus => {
	if (isCreditOrder(order)) {
		const status = order.creditApplicationStatus ?? CreditApplicationStatus.Unapplied;
		return { family: "creditApplication", label: formatCreditApplicationStatus(status), status };
	}
	if (!order.paymentStatus) return null;
	return { family: "payment", label: formatPaymentStatus(order.paymentStatus), status: order.paymentStatus };
};

export const formatOrderMoneyStatus = (order: OrderMoneyStatusFields): string =>
	resolveOrderMoneyStatus(order)?.label ?? "";
